import { useState, useEffect, useCallback } from 'react';
import { useSession } from '@clerk/clerk-react';
import { fetchWithAuth } from 'shared/api/fetch-with-auth';
import { API_URL } from 'constants/index';

export interface TerminationUnit {
  unitId: string;
  unitLabel: string;
  floor: number;
  buildingId: string;
  buildingName: string;
  contractId: number;
  contractEndDate: string | null;
  terminationStartedAt: string | null;
  tenantName: string;
  tenantEmail: string;
  outgoingChecklistSent: boolean;
  outgoingInspectionDate: string | null;
  outgoingRentalFileStatus: string;
  outgoingResponsibleEmail: string | null;
  guaranteeReleased: boolean;
  activeCandidateCount: number;
}

export interface VacantUnit {
  unitId: string;
  unitLabel: string;
  floor: number;
  buildingId: string;
  buildingName: string;
  advertisedRentalPrice: number | null;
  vacantDueToRenovation: boolean;
  vacantSince: string | null;
}

export interface CandidateUnit extends VacantUnit {
  candidates: {
    applicationId: number;
    firstName: string;
    lastName: string;
    status: string;
  }[];
}

export interface IncomingUnit {
  unitId: string;
  unitLabel: string;
  floor: number;
  buildingId: string;
  buildingName: string;
  advertisedRentalPrice: number | null;
  contractId: number;
  applicationId: number;
  tenantName: string;
  tenantEmail: string;
  incomingContractSigned: boolean;
  incomingInspectionDate: string | null;
  incomingGuaranteeReceived: boolean;
  incomingFireInsuranceReceived: boolean;
  incomingRentalFileStatus: string;
}

export interface HerverhuurPipeline {
  inTermination: TerminationUnit[];
  outgoingInspection: TerminationUnit[];
  vacant: VacantUnit[];
  withCandidates: CandidateUnit[];
  incomingInspection: IncomingUnit[];
}

export const useHerverhuurPipeline = (buildingId?: string) => {
  const { session } = useSession();
  const [pipeline, setPipeline] = useState<HerverhuurPipeline | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const fetchPipeline = useCallback(async () => {
    if (!session) return;
    try {
      setIsLoading(true);
      const params = buildingId ? `?buildingId=${encodeURIComponent(buildingId)}` : '';
      const response = await fetchWithAuth(
        `${API_URL}/rental/herverhuur-pipeline${params}`,
        { method: 'GET' },
        session
      );
      if (!response.ok) throw new Error(`Fout bij ophalen pipeline: ${response.status}`);
      const data = await response.json();
      setPipeline(data);
      setError(null);
    } catch (err) {
      console.error('useHerverhuurPipeline error:', err);
      setError('Kon de herverhuur pipeline niet laden');
    } finally {
      setIsLoading(false);
    }
  }, [session, buildingId]);

  useEffect(() => {
    fetchPipeline();
  }, [fetchPipeline]);

  return { pipeline, isLoading, error, refetch: fetchPipeline };
};
